import {IVanillaReaderUI} from './IVanillaReaderUI';
import {IVanillaReaderUiComponentController} from './IVanillaReaderUiComponentController';
import {VanillaReaderUI} from './VanillaReaderUI';
import {ILocatorData} from "@colibrio/colibrio-reader-framework/colibrio-core-locator";

export interface VanillaReaderUiMenuMediaPlayerController extends IVanillaReaderUiComponentController, IVanillaReaderUI {
}

/**
 * # VanillaReaderUiMenuMediaPlayerController
 *
 * ## RESPONSIBILITIES
 * This controller mixing class handles all event callbacks from the `VanillaReaderUiMenuMediaPlayer` class.
 * It also holds all the playback related methods to that send intents to the `VanillaReader` to trigger behaviours
 * such as play and pause.
 *
 * Just as all other controller mixin classes, this class is used to add functionality to the `VanillaReaderUi` class
 * in a "modular" way. Read more about this in the `UI Components and Controller Mixins` in the README.md file.
 *
 * ## RELATED TYPES
 * - VanillaReaderUi
 * - VanillaReaderUiMenuMediaPlayer
 */
export class VanillaReaderUiMenuMediaPlayerController {

    static __initialize__(vanillaReaderUiContext: VanillaReaderUI): void {
        vanillaReaderUiContext.menuMediaPlayer.onOpened(vanillaReaderUiContext._onMenuMediaPlayerOpened.bind(vanillaReaderUiContext));
        vanillaReaderUiContext.menuMediaPlayer.onClosed(vanillaReaderUiContext._onMenuMediaPlayerClosed.bind(vanillaReaderUiContext));
        vanillaReaderUiContext.menuMediaPlayer.onPlay(vanillaReaderUiContext._onMenuMediaPlayerPlayIntent.bind(vanillaReaderUiContext));
        vanillaReaderUiContext.menuMediaPlayer.onPause(vanillaReaderUiContext._onMenuMediaPlayerPauseIntent.bind(vanillaReaderUiContext));
        vanillaReaderUiContext.menuMediaPlayer.onSeekNext(vanillaReaderUiContext._onMenuMediaPlayerSeekNextIntent.bind(vanillaReaderUiContext));
        vanillaReaderUiContext.menuMediaPlayer.onSeekPrevious(vanillaReaderUiContext._onMenuMediaPlayerPreviousIntent.bind(vanillaReaderUiContext));

    }

    startPlayback(locator?: string): void {
        this.vanillaReaderAPI?.startMediaPlayback(locator);
    }

    pausePlayback(): void {
        this.vanillaReaderAPI?.pauseMediaPlayback();
    }

    escapeActiveSegment(): void {
        let contextPathArrayLength = this.mediaPlayerEscapabilityContext.length;
        if(contextPathArrayLength > 0) {
            let previousPathIndex = contextPathArrayLength > 1 ? contextPathArrayLength - 1 : 0;
            this.seekToLocator(this.mediaPlayerEscapabilityContext[previousPathIndex].blockEndLocator);
        }
    }

    escapeAll(): void {
        let contextPathArrayLength = this.mediaPlayerEscapabilityContext.length;
        if(contextPathArrayLength > 0) {
            this.seekToLocator(this.mediaPlayerEscapabilityContext[0].blockEndLocator);
        }
    }

    seekNext(): void {
        this.vanillaReaderAPI?.seekMediaPlaybackNext();
    }

    seekPrevious(): void {
        this.vanillaReaderAPI?.seekMediaPlaybackPrevious();
    }

    seekForward(): void {
        this.vanillaReaderAPI?.seekMediaPlaybackForward();
    }

    seekBackward(): void
    {
        this.vanillaReaderAPI?.seekMediaPlaybackBackward();
    }

    seekToLocator(locator: ILocatorData): void {
        this.vanillaReaderAPI?.seekMediaToLocator(locator);
    }

    seekToLocatorWithinVisibleRange(locator: ILocatorData): void {
        this.vanillaReaderAPI?.seekMediaToLocatorWithinVisibleRange(locator);
    }

    setPlaybackRate(rate: number): void {
        this.vanillaReaderAPI?.setMediaPlaybackRate(rate);
    }

    setPlaybackVolume(volume: number): void {
        this.vanillaReaderAPI?.setMediaPlaybackVolume(volume);
    }

    setTtsVoice(voiceName: string): void {
        this.vanillaReaderAPI?.setTtsVoice(voiceName);
    }

    togglePlayback(activate: boolean): void {
        this.vanillaReaderAPI?.toggleMediaPlayer(activate, {rate: this.dialogSettings.activePlaybackRateOption});
    }

    _onMenuMediaPlayerOpened(): void {
        this.fabMediaPlayer.stateIsPlaying = false;
        this.fabMediaPlayer.show();

        this.togglePlayback(true);

        // Save the updated option value
        let updatedUiOptions = this.serializeToUiOptionsData();
        this.vanillaReaderOptionsDataStore.setVanillaReaderUiOptionsData(updatedUiOptions).catch(console.warn);

    };

    _onMenuMediaPlayerClosed(): void {
        this.fabMediaPlayer.stateIsPlaying = false;
        this.fabMediaPlayer.hide();
        this.togglePlayback(false);

        // Save the updated option value
        let updatedUiOptions = this.serializeToUiOptionsData();
        this.vanillaReaderOptionsDataStore.setVanillaReaderUiOptionsData(updatedUiOptions).catch(console.warn);

    };

    _onMenuMediaPlayerPlayIntent(locator?: string): void {
        this.startPlayback(locator);
    };

    _onMenuMediaPlayerPauseIntent(): void {
        this.pausePlayback();
    };

    _onMenuMediaPlayerSeekNextIntent(): void {
        this.seekNext();
    };

    _onMenuMediaPlayerPreviousIntent(): void {
        this.seekPrevious();
    };

    _onMediaPlayerIsReadyToPlay(): void {
        this.menuMediaPlayer.enable();
        if (this.menuMediaPlayer.isOpen() && this.mediaPlayerShouldStartPlaybackWhenReady) {
            this._onMenuMediaPlayerPlayIntent();
        }
    };
}
